import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { memberApi, departmentApi, specialGroupApi, lcClassApi, Member, MemberRequest, SpecialGroupMember, LcClass } from '@/api/b2b'
import { Plus, Pencil, Search, Upload, Download, ChevronLeft, ChevronRight, AlertCircle, X, UserMinus, Mail, KeyRound, MoreVertical, Trash2, Users } from 'lucide-react'
import { Dialog, DialogContent } from '@quebon/ui/components/dialog'
import { toast } from 'sonner'
import { getApiErrorMessage } from '@/utils/apiError'
import { CsvColumn, csvTimestamp, downloadCsv, toCsv } from '@/utils/csv'

// 다운로드 컬럼 정의 (향후 컬럼 변경 시 이 상수만 수정)
const MEMBER_DOWNLOAD_COLUMNS: CsvColumn<Member>[] = [
  { key: 'fullName', label: '이름' },
  { key: 'email', label: '이메일' },
  { key: 'specialGroupNames', label: '학습그룹', value: (m) => (m.specialGroupNames ?? []).join(', ') },
]

export default function MemberManagement() {
  const { t } = useTranslation('admin')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false)
  const [isClassModalOpen, setIsClassModalOpen] = useState(false)
  const [selectedMemberForClass, setSelectedMemberForClass] = useState<{ userId: number; fullName: string } | null>(null)
  const [selectedClassId, setSelectedClassId] = useState<number | null>(null)
  // {t('member.leaveClass')} 모달
  const [isLeaveClassModalOpen, setIsLeaveClassModalOpen] = useState(false)
  const [leaveClassInfo, setLeaveClassInfo] = useState<{
    userId: number
    fullName: string
    classId: number
    className: string
    productName: string | null
  } | null>(null)
  const [editingMember, setEditingMember] = useState<number | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null)
  const [selectedSpecialGroupId, setSelectedSpecialGroupId] = useState<number | null>(null)
  const [selectedAccountStatus, setSelectedAccountStatus] = useState<string | null>(null)
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<number>>(new Set())
  const [isSendCredentialsModalOpen, setIsSendCredentialsModalOpen] = useState(false)
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false)
  const [resetPasswordTarget, setResetPasswordTarget] = useState<{ userId: number; fullName: string } | null>(null)
  const [resetPasswordValue, setResetPasswordValue] = useState('12341234')
  const [bulkDefaults, setBulkDefaults] = useState({
    role: 'member',
  })
  const [formData, setFormData] = useState<MemberRequest>({
    email: '',
    fullName: '',
    departmentId: undefined,
    employeeId: '',
    position: '',
    role: 'member',
    phoneNumber: '',
  })
  const [editAccountStatus, setEditAccountStatus] = useState<string>('PENDING')
  const [editFormSpecialGroupIds, setEditFormSpecialGroupIds] = useState<number[]>([])
  const [originalSpecialGroupIds, setOriginalSpecialGroupIds] = useState<number[]>([])
  const [page, setPage] = useState(0)
  const pageSize = 20
  const [isAllFilteredSelected, setIsAllFilteredSelected] = useState(false)
  const [openMenuId, setOpenMenuId] = useState<number | null>(null)
  const [dropUp, setDropUp] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const menuButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (openMenuId === null) return
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openMenuId])

  const { data: membersData, isLoading } = useQuery({
    queryKey: ['members'],
    queryFn: () => memberApi.getAll(),
  })

  const { data: departmentsData } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentApi.getAll(),
  })

  const { data: specialGroupsData } = useQuery({
    queryKey: ['special-groups'],
    queryFn: () => specialGroupApi.getAll(),
  })

  // 선택된 학습그룹의 멤버 조회
  const { data: specialGroupMembersData } = useQuery({
    queryKey: ['special-group-members', selectedSpecialGroupId],
    queryFn: () => selectedSpecialGroupId ? specialGroupApi.getMembers(selectedSpecialGroupId) : null,
    enabled: selectedSpecialGroupId !== null,
  })

  // 클래스 목록 조회 (클래스에 추가 기능용)
  const { data: classesData, isLoading: isLoadingClasses } = useQuery({
    queryKey: ['classes'],
    queryFn: () => lcClassApi.getAll(),
  })

  const createMutation = useMutation({
    mutationFn: (data: MemberRequest) => memberApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] })
      toast.success(t('member.toast.created'))
      closeModal()
    },
    onError: (error: any) => {
      console.error('직원 등록 실패:', error)
      toast.error(getApiErrorMessage(error, t))
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: number; data: any }) =>
      memberApi.update(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] })
      toast.success(t('member.toast.updated'))
      closeModal()
    },
    onError: (error: any) => {
      console.error('직원 수정 실패:', error)
      toast.error(getApiErrorMessage(error, t))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (userId: number) => memberApi.delete(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] })
      toast.success(t('member.toast.deleted'))
    },
    onError: (error: any) => {
      console.error('삭제 실패:', error)
      toast.error(getApiErrorMessage(error, t))
    },
  })

  const addToClassMutation = useMutation({
    mutationFn: ({ classId, userIds }: { classId: number; userIds: number[] }) =>
      lcClassApi.addMembers(classId, userIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] })
      queryClient.invalidateQueries({ queryKey: ['classes'] })
      toast.success(t('member.toast.addedToClass'))
      closeClassModal()
    },
    onError: (error: any) => {
      console.error('클래스 추가 실패:', error)
      toast.error(getApiErrorMessage(error, t))
    },
  })

  const removeFromClassMutation = useMutation({
    mutationFn: ({ classId, userIds }: { classId: number; userIds: number[] }) =>
      lcClassApi.removeMembers(classId, userIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] })
      queryClient.invalidateQueries({ queryKey: ['classes'] })
      toast.success(t('member.toast.leftClass'))
      closeLeaveClassModal()
    },
    onError: (error: any) => {
      console.error('Leave class failed:', error)
      toast.error(getApiErrorMessage(error, t))
    },
  })

  const bulkCreateMutation = useMutation({
    mutationFn: (members: MemberRequest[]) => memberApi.createBulk(members),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['members'] })
      const result = response.data.data
      const successCount = result.success.length
      const failedCount = result.failed.length

      if (failedCount === 0) {
        toast.success(t('member.toast.bulkSuccess', { count: successCount }))
      } else if (successCount === 0) {
        const failDetails = result.failed.map(f => `• ${f.email} (${f.fullName}): ${f.reason}`).join(' ')
        toast.error(t('member.toast.bulkAllFailed', { details: failDetails }))
      } else {
        const failDetails = result.failed.map(f => `• ${f.email} (${f.fullName}): ${f.reason}`).join(' ')
        toast.warning(t('member.toast.bulkPartial', { success: successCount, failed: failedCount, details: failDetails }))
      }

      setIsBulkModalOpen(false)
      setCsvFile(null)
    },
    onError: (error: any) => {
      console.error('벌크 등록 실패:', error)
      toast.error(getApiErrorMessage(error, t))
    },
  })

  const addToSpecialGroupMutation = useMutation({
    mutationFn: ({ groupId, userIds }: { groupId: number; userIds: number[] }) =>
      specialGroupApi.addMembers(groupId, userIds),
  })

  const removeFromSpecialGroupMutation = useMutation({
    mutationFn: ({ groupId, userIds }: { groupId: number; userIds: number[] }) =>
      specialGroupApi.removeMembers(groupId, userIds),
  })

  const sendCredentialsMutation = useMutation({
    mutationFn: (userIds: number[]) => memberApi.sendCredentials(userIds),
    onSuccess: (response) => {
      const data = response.data?.data
      if (data) {
        if (data.failed > 0) {
          toast.error(t('member.toast.credentialsPartial', { sent: data.sent, failed: data.failed }))
        } else {
          toast.success(t('member.toast.credentialsSent', { count: data.sent }))
        }
      }
      setIsSendCredentialsModalOpen(false)
      setSelectedMemberIds(new Set())
    },
    onError: (error: any) => {
      console.error('Send credentials failed:', error)
      toast.error(getApiErrorMessage(error, t))
      setIsSendCredentialsModalOpen(false)
    },
  })

  const resetPasswordMutation = useMutation({
    mutationFn: ({ userId, newPassword }: { userId: number; newPassword?: string }) =>
      memberApi.resetPassword(userId, newPassword),
    onSuccess: (response) => {
      const tempPassword = response.data?.data?.temporaryPassword
      toast.success(t('member.toast.resetPasswordSuccess', { password: tempPassword }))
      setIsResetPasswordModalOpen(false)
      setResetPasswordTarget(null)
      setResetPasswordValue('12341234')
    },
    onError: (error: any) => {
      console.error('Reset password failed:', error)
      toast.error(getApiErrorMessage(error, t))
    },
  })

  const members = membersData?.data?.data || []
  const departments = departmentsData?.data?.data || []
  const specialGroups = specialGroupsData?.data?.data || []
  const specialGroupMembers = specialGroupMembersData?.data?.data || []
  const classes = (classesData?.data?.data || []) as LcClass[]

  // 학습그룹 멤버 ID 목록 (필터링용)
  const specialGroupMemberIds = new Set(specialGroupMembers.map((m: SpecialGroupMember) => m.userId))

  const openClassModal = (member: typeof members[0]) => {
    setSelectedMemberForClass({ userId: member.userId, fullName: member.fullName })
    setSelectedClassId(null)
    setIsClassModalOpen(true)
  }

  const closeClassModal = () => {
    setIsClassModalOpen(false)
    setSelectedMemberForClass(null)
    setSelectedClassId(null)
  }

  const handleAddToClass = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedMemberForClass || !selectedClassId) return

    addToClassMutation.mutate({
      classId: selectedClassId,
      userIds: [selectedMemberForClass.userId],
    })
  }

  // {t('member.leaveClass')} 모달 열기
  const openLeaveClassModal = (member: typeof members[0], className: string) => {
    // className으로 classId 찾기
    const targetClass = classes.find(cls => cls.name === className)
    if (!targetClass) {
      toast.warning(t('member.toast.classNotFound'))
      return
    }

    // 해당 클래스의 상품명 가져오기
    const productName = member.classProductMap?.[className] || null

    setLeaveClassInfo({
      userId: member.userId,
      fullName: member.fullName,
      classId: targetClass.id,
      className,
      productName,
    })
    setIsLeaveClassModalOpen(true)
  }

  const closeLeaveClassModal = () => {
    setIsLeaveClassModalOpen(false)
    setLeaveClassInfo(null)
  }

  const handleLeaveClass = () => {
    if (!leaveClassInfo) return
    
    removeFromClassMutation.mutate({
      classId: leaveClassInfo.classId,
      userIds: [leaveClassInfo.userId],
    })
  }

  const filteredMembers = members.filter((m) => {
    // 검색어 필터
    const matchesSearch = searchTerm === '' ||
      m.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.email.toLowerCase().includes(searchTerm.toLowerCase())

    // 부서 필터
    const matchesDepartment = selectedDepartmentId === null || m.departmentId === selectedDepartmentId

    // 학습그룹 필터
    const matchesSpecialGroup = selectedSpecialGroupId === null || specialGroupMemberIds.has(m.userId)

    // 계정 상태 필터
    const matchesAccountStatus = selectedAccountStatus === null || m.accountStatus === selectedAccountStatus

    return matchesSearch && matchesDepartment && matchesSpecialGroup && matchesAccountStatus
  }).sort((a, b) => {
    const dateA = a.joinedAt ? new Date(a.joinedAt).getTime() : 0
    const dateB = b.joinedAt ? new Date(b.joinedAt).getTime() : 0
    return dateB - dateA
  })

  const totalPages = Math.ceil(filteredMembers.length / pageSize)
  const paginatedMembers = filteredMembers.slice(page * pageSize, (page + 1) * pageSize)

  const toggleMemberSelection = (userId: number) => {
    const newSelected = new Set(selectedMemberIds)
    if (newSelected.has(userId)) {
      newSelected.delete(userId)
      setIsAllFilteredSelected(false)
    } else {
      newSelected.add(userId)
    }
    setSelectedMemberIds(newSelected)
  }

  const toggleAllMembers = () => {
    const pageUserIds = paginatedMembers.map(m => m.userId)
    const allSelected = pageUserIds.every(id => selectedMemberIds.has(id))
    if (allSelected) {
      const newSelected = new Set(selectedMemberIds)
      pageUserIds.forEach(id => newSelected.delete(id))
      setSelectedMemberIds(newSelected)
      setIsAllFilteredSelected(false)
    } else {
      const newSelected = new Set(selectedMemberIds)
      pageUserIds.forEach(id => newSelected.add(id))
      setSelectedMemberIds(newSelected)
    }
  }

  const selectAllFiltered = () => {
    const newSelected = new Set(selectedMemberIds)
    filteredMembers.forEach(m => newSelected.add(m.userId))
    setSelectedMemberIds(newSelected)
    setIsAllFilteredSelected(true)
  }

  const clearAllSelection = () => {
    setSelectedMemberIds(new Set())
    setIsAllFilteredSelected(false)
  }

  const openSendCredentialsModal = () => {
    if (selectedMemberIds.size === 0) {
      toast.error(t('member.toast.selectMembers'))
      return
    }
    setIsSendCredentialsModalOpen(true)
  }

  const handleDownloadMembers = () => {
    try {
      const csv = toCsv(filteredMembers, MEMBER_DOWNLOAD_COLUMNS)
      downloadCsv(`members_${csvTimestamp()}.csv`, csv)
      toast.success(t('member.toast.downloadSuccess', { count: filteredMembers.length }))
    } catch (error) {
      toast.error(t('member.toast.downloadError', { error: getApiErrorMessage(error, t) }))
    }
  }

  const handleSendCredentials = () => {
    sendCredentialsMutation.mutate(Array.from(selectedMemberIds))
  }

  const openCreateModal = () => {
    setEditingMember(null)
    setFormData({
      email: '',
      fullName: '',
      departmentId: undefined,
      employeeId: '',
      position: '',
      role: 'member',
      phoneNumber: '',
    })
    setIsModalOpen(true)
  }

  const openEditModal = async (member: typeof members[0]) => {
    setEditingMember(member.userId)
    setFormData({
      email: member.email,
      fullName: member.fullName,
      departmentId: member.departmentId || undefined,
      employeeId: member.employeeId || '',
      position: member.position || '',
      role: member.role,
      phoneNumber: member.phoneNumber || '',
    })
    setEditAccountStatus(member.accountStatus || 'PENDING')
    
    // 해당 사용자가 속한 학습그룹 조회
    const userSpecialGroupIds: number[] = []
    for (const group of specialGroups) {
      try {
        const response = await specialGroupApi.getMembers(group.id)
        const members = response.data?.data || []
        if (members.some((m: SpecialGroupMember) => m.userId === member.userId)) {
          userSpecialGroupIds.push(group.id)
        }
      } catch (e) {
        console.error(`Failed to fetch members for group ${group.id}:`, e)
      }
    }
    setEditFormSpecialGroupIds(userSpecialGroupIds)
    setOriginalSpecialGroupIds(userSpecialGroupIds)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingMember(null)
    setEditAccountStatus('PENDING')
    setEditFormSpecialGroupIds([])
    setOriginalSpecialGroupIds([])
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (editingMember) {
      // 학습그룹 변경사항 처리
      const addedGroups = editFormSpecialGroupIds.filter(id => !originalSpecialGroupIds.includes(id))
      const removedGroups = originalSpecialGroupIds.filter(id => !editFormSpecialGroupIds.includes(id))
      
      // 학습그룹 추가
      for (const groupId of addedGroups) {
        try {
          await addToSpecialGroupMutation.mutateAsync({ groupId, userIds: [editingMember] })
        } catch (e) {
          console.error(`Failed to add to special group ${groupId}:`, e)
        }
      }
      
      // 학습그룹 제거
      for (const groupId of removedGroups) {
        try {
          await removeFromSpecialGroupMutation.mutateAsync({ groupId, userIds: [editingMember] })
        } catch (e) {
          console.error(`Failed to remove from special group ${groupId}:`, e)
        }
      }
      
      updateMutation.mutate({
        userId: editingMember,
        data: {
          fullName: formData.fullName.trim() || undefined,
          departmentId: formData.departmentId,
          employeeId: formData.employeeId,
          position: formData.position,
          role: formData.role,
          phoneNumber: formData.phoneNumber?.trim() || undefined,
          accountStatus: editAccountStatus,
        },
      })
    } else {
      // undefined 값을 제거하고 빈 문자열은 undefined로 처리
      const submitData: MemberRequest = {
        email: formData.email.trim(),
        fullName: formData.fullName.trim(),
        departmentId: formData.departmentId,
        employeeId: formData.employeeId?.trim() || undefined,
        position: formData.position?.trim() || undefined,
        role: formData.role || 'member',
        phoneNumber: formData.phoneNumber?.trim() || undefined,
      }
      // 빈 문자열을 undefined로 변환
      if (submitData.employeeId === '') submitData.employeeId = undefined
      if (submitData.position === '') submitData.position = undefined
      if (submitData.phoneNumber === '') submitData.phoneNumber = undefined

      console.log('[MemberManagement] Submitting data:', JSON.stringify(submitData, null, 2))
      createMutation.mutate(submitData)
    }
  }

  const handleDelete = (userId: number, name: string) => {
    const displayName = name || t('member.noName')
    if (confirm(t('member.confirmDelete', { name: displayName }))) {
      deleteMutation.mutate(userId)
    }
  }

  const parseCSV = (text: string): MemberRequest[] => {
    const lines = text.split('\n').filter(line => line.trim())
    if (lines.length === 0) return []

    // 헤더 제거 (첫 번째 줄)
    const dataLines = lines.slice(1)
    
    return dataLines.map((line, index) => {
      const values = line.split(',').map(v => v.trim())
      
      // CSV 형식: 이메일, 이름, 부서ID(선택), 사번(선택), 직책(선택), 역할(선택)
      const email = values[0] || ''
      const fullName = values[1] || ''
      const departmentId = values[2] ? Number(values[2]) : undefined
      const employeeId = values[3] || undefined
      const position = values[4] || undefined
      const role = values[5] || bulkDefaults.role

      if (!email || !fullName) {
        throw new Error(`${index + 2}번째 줄: 이메일과 이름은 필수입니다`)
      }

      return {
        email,
        fullName,
        departmentId,
        employeeId,
        position,
        role: role === 'admin' ? 'admin' : bulkDefaults.role,
      }
    }).filter(member => member.email && member.fullName)
  }

  const handleBulkUpload = async () => {
    if (!csvFile) {
      toast.warning(t('member.toast.noFile'))
      return
    }

    const reader = new FileReader()
    reader.onload = async (e) => {
      try {
        const text = e.target?.result as string
        const members = parseCSV(text)

        if (members.length === 0) {
          toast.warning(t('member.toast.noData'))
          return
        }

        bulkCreateMutation.mutate(members)
      } catch (error: any) {
        toast.error(t('member.toast.csvParseError', { error: error.message }))
      }
    }
    reader.readAsText(csvFile, 'UTF-8')
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('member.title')}</h1>
          <p className="mt-1 text-gray-500">{t('member.description')}</p>
        </div>
        <div className="flex items-center space-x-3">
          {selectedMemberIds.size > 0 && (
            <button
              onClick={openSendCredentialsModal}
              className="flex items-center px-4 py-2 space-x-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              <Mail className="w-5 h-5" />
              <span>{t('member.sendCredentials', { count: selectedMemberIds.size })}</span>
            </button>
          )}
          <button
            onClick={handleDownloadMembers}
            className="flex items-center px-4 py-2 space-x-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Download className="w-5 h-5" />
            <span>{t('member.downloadButton')}</span>
          </button>
          <button
            onClick={() => setIsBulkModalOpen(true)}
            className="flex items-center px-4 py-2 space-x-2 text-white bg-gray-600 rounded-lg hover:bg-gray-700"
          >
            <Upload className="w-5 h-5" />
            <span>{t('member.bulkButton')}</span>
          </button>
          <button
            onClick={openCreateModal}
            className="flex items-center px-4 py-2 space-x-2 text-white rounded-lg bg-primary-600 hover:bg-primary-700"
          >
            <Plus className="w-5 h-5" />
            <span>{t('member.addButton')}</span>
          </button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex gap-3 items-center">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder={t('member.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(0); setIsAllFilteredSelected(false) }}
            className="py-2 pr-4 pl-10 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Department Filter */}
        <div className="relative">
          <select
            value={selectedDepartmentId ?? ''}
            onChange={(e) => { setSelectedDepartmentId(e.target.value ? Number(e.target.value) : null); setPage(0); setIsAllFilteredSelected(false) }}
            className="pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white min-w-[150px]"
          >
            <option value="">{t('member.filter.department')}</option>
            {departments.map((dept) => (
              <option key={dept.id} value={dept.id}>
                {dept.name} ({t('member.filter.count', { count: dept.memberCount })})
              </option>
            ))}
          </select>
        </div>

        {/* Special Group Filter */}
        <div className="relative">
          <select
            value={selectedSpecialGroupId ?? ''}
            onChange={(e) => { setSelectedSpecialGroupId(e.target.value ? Number(e.target.value) : null); setPage(0); setIsAllFilteredSelected(false) }}
            className="pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white min-w-[150px]"
          >
            <option value="">{t('member.filter.specialGroup')}</option>
            {specialGroups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name} ({t('member.filter.count', { count: group.memberCount })})
              </option>
            ))}
          </select>
        </div>

        {/* Account Status Filter */}
        <div className="relative">
          <select
            value={selectedAccountStatus ?? ''}
            onChange={(e) => { setSelectedAccountStatus(e.target.value || null); setPage(0); setIsAllFilteredSelected(false) }}
            className="pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white min-w-[120px]"
          >
            <option value="">{t('member.filter.accountStatus')}</option>
            <option value="PENDING">{t('member.status.pending')}</option>
            <option value="INVITED">{t('member.status.invited')}</option>
            <option value="ACTIVE">{t('member.status.active')}</option>
          </select>
        </div>

        {/* Clear Filter Button */}
        {(selectedDepartmentId !== null || selectedSpecialGroupId !== null || selectedAccountStatus !== null) && (
          <button
            onClick={() => {
              setSelectedDepartmentId(null)
              setSelectedSpecialGroupId(null)
              setSelectedAccountStatus(null)
              setPage(0)
            }}
            className="p-2 text-gray-400 rounded-lg hover:text-gray-600 hover:bg-gray-100"
            title={t('member.filter.clearTitle')}
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Active Filter Badges */}
      {(selectedDepartmentId !== null || selectedSpecialGroupId !== null || selectedAccountStatus !== null) && (
        <div className="flex flex-wrap gap-2 items-center">
          {selectedDepartmentId !== null && (
            <span className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-700 bg-blue-100 rounded-full">
              {t('member.filter.departmentLabel', { name: departments.find(d => d.id === selectedDepartmentId)?.name || '' })}
              <button
                onClick={() => setSelectedDepartmentId(null)}
                className="ml-2 text-blue-500 hover:text-blue-700"
              >
                <X className="w-4 h-4" />
              </button>
            </span>
          )}
          {selectedSpecialGroupId !== null && (
            <span className="inline-flex items-center px-3 py-1 text-sm font-medium text-purple-700 bg-purple-100 rounded-full">
              {t('member.filter.groupLabel', { name: specialGroups.find(g => g.id === selectedSpecialGroupId)?.name || '' })}
              <button
                onClick={() => setSelectedSpecialGroupId(null)}
                className="ml-2 text-purple-500 hover:text-purple-700"
              >
                <X className="w-4 h-4" />
              </button>
            </span>
          )}
          {selectedAccountStatus !== null && (
            <span className="inline-flex items-center px-3 py-1 text-sm font-medium text-green-700 bg-green-100 rounded-full">
              {t('member.filter.statusLabel', { status: selectedAccountStatus === 'PENDING' ? t('member.status.pending') : selectedAccountStatus === 'INVITED' ? t('member.status.invited') : t('member.status.active') })}
              <button
                onClick={() => setSelectedAccountStatus(null)}
                className="ml-2 text-green-500 hover:text-green-700"
              >
                <X className="w-4 h-4" />
              </button>
            </span>
          )}
          <span className="text-sm text-gray-500">
            {t('member.filter.count', { count: filteredMembers.length })}
          </span>
        </div>
      )}

      {/* Member List */}
      <div className="overflow-visible bg-white rounded-xl shadow-sm">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">{t('common.loading')}</div>
        ) : filteredMembers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {searchTerm ? t('member.noSearchResult') : t('member.empty')}
          </div>
        ) : (
          <table className="w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 w-10 text-left rounded-tl-xl">
                  <input
                    type="checkbox"
                    checked={paginatedMembers.length > 0 && paginatedMembers.every(m => selectedMemberIds.has(m.userId))}
                    onChange={toggleAllMembers}
                    className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[24%]">{t('member.table.nameEmail')}</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[16%]">{t('member.table.deptId')}</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[10%]">{t('member.table.status')}</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[18%]">{t('member.table.class')}</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[16%]">{t('member.table.group')}</th>
                <th className="px-3 py-3 w-16 text-xs font-medium text-right text-gray-500 uppercase rounded-tr-xl">{t('member.table.actions')}</th>
              </tr>
            </thead>
            {/* Gmail-style 전체 선택 배너 */}
            {paginatedMembers.length > 0 && paginatedMembers.every(m => selectedMemberIds.has(m.userId)) && filteredMembers.length > paginatedMembers.length && (
              <thead>
                <tr>
                  <th colSpan={7} className="px-3 py-2 bg-blue-50 border-b border-blue-100">
                    {isAllFilteredSelected ? (
                      <p className="text-sm text-center text-blue-700">
                        {t('member.selectAll.allSelected', { count: filteredMembers.length })}{' '}
                        <button
                          onClick={clearAllSelection}
                          className="font-medium text-blue-600 underline hover:text-blue-800"
                        >
                          {t('member.selectAll.deselect')}
                        </button>
                      </p>
                    ) : (
                      <p className="text-sm text-center text-blue-700">
                        {t('member.selectAll.pageSelected', { count: paginatedMembers.length })}{' '}
                        <button
                          onClick={selectAllFiltered}
                          className="font-medium text-blue-600 underline hover:text-blue-800"
                        >
                          {t('member.selectAll.selectAll', { count: filteredMembers.length })}
                        </button>
                      </p>
                    )}
                  </th>
                </tr>
              </thead>
            )}
            <tbody className="divide-y divide-gray-200">
              {paginatedMembers.map((member) => (
                    <tr key={member.userId} className="hover:bg-gray-50">
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={selectedMemberIds.has(member.userId)}
                          onChange={() => toggleMemberSelection(member.userId)}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-col min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-gray-900 truncate">{member.fullName}</span>
                            {member.role === 'admin' && (
                              <span className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-700 flex-shrink-0">
                                {t('member.role.admin')}
                              </span>
                            )}
                            {member.role === 'admin_learner' && (
                              <span className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700 flex-shrink-0">
                                {t('member.role.adminLearner')}
                              </span>
                            )}
                            {member.role === 'member' && (
                              <span className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700 flex-shrink-0">
                                {t('member.role.learner')}
                              </span>
                            )}
                          </div>
                          <span className="text-sm text-gray-500 mt-0.5 truncate">{member.email}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-col min-w-0">
                          <span className="text-gray-500 truncate">{member.departmentName || '-'}</span>
                          <span className="text-sm text-gray-500 mt-0.5 truncate">{member.employeeId || '-'}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        {member.accountStatus === 'PENDING' && (
                          <span className="inline-flex px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full">
                            {t('member.status.pending')}
                          </span>
                        )}
                        {member.accountStatus === 'INVITED' && (
                          <span className="inline-flex px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
                            {t('member.status.invited')}
                          </span>
                        )}
                        {member.accountStatus === 'ACTIVE' && (
                          <span className="inline-flex px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                            {t('member.status.active')}
                          </span>
                        )}
                        {!member.accountStatus && (
                          <span className="inline-flex px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full">
                            -
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-col space-y-1 min-w-0">
                          {member.classNames && member.classNames.length > 0 ? (
                            member.classNames.map((className, idx) => {
                              const targetClass = classes.find(cls => cls.name === className)
                              return (
                                <div key={idx} className="flex gap-1 items-center min-w-0 group">
                                  {targetClass ? (
                                    <button
                                      onClick={() => navigate('/admin/classes', { state: { selectClassId: targetClass.id } })}
                                      className="text-sm text-left truncate text-primary-600 hover:text-primary-800 hover:underline"
                                      title={className}
                                    >
                                      {className}
                                    </button>
                                  ) : (
                                    <span
                                      className="text-sm text-gray-900 truncate"
                                      title={className}
                                    >
                                      {className}
                                    </span>
                                  )}
                                  <button
                                    onClick={() => openLeaveClassModal(member, className)}
                                    className="p-0.5 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                                    title={t('member.leaveClass')}
                                  >
                                    <UserMinus className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              )
                            })
                          ) : (
                            <span className="text-sm text-gray-400">{t('member.table.noClass')}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-1 min-w-0">
                          {member.specialGroupNames && member.specialGroupNames.length > 0 ? (
                            member.specialGroupNames.map((groupName, idx) => (
                              <span
                                key={idx}
                                className="inline-flex px-2 py-0.5 text-xs font-medium text-purple-700 bg-purple-50 rounded-full truncate max-w-full"
                                title={groupName}
                              >
                                {groupName}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex relative justify-end" ref={openMenuId === member.userId ? menuRef : undefined}>
                          <button
                            ref={openMenuId === member.userId ? menuButtonRef : undefined}
                            onClick={(e) => {
                              e.stopPropagation()
                              if (openMenuId !== member.userId) {
                                const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect()
                                const menuHeight = 200
                                setDropUp(rect.bottom + menuHeight > window.innerHeight)
                              }
                              setOpenMenuId(openMenuId === member.userId ? null : member.userId)
                            }}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          {openMenuId === member.userId && (
                            <div className={`absolute right-0 ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'} w-36 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20`}>
                              <button
                                onClick={() => {
                                  setResetPasswordTarget({ userId: member.userId, fullName: member.fullName })
                                  setResetPasswordValue('12341234')
                                  setIsResetPasswordModalOpen(true)
                                  setOpenMenuId(null)
                                }}
                                className="flex gap-2 items-center px-4 py-2 w-full text-sm text-left text-gray-700 hover:bg-gray-100"
                              >
                                <KeyRound className="w-3.5 h-3.5" />
                                {t('member.menu.resetPassword')}
                              </button>
                              <button
                                onClick={() => {
                                  openClassModal(member)
                                  setOpenMenuId(null)
                                }}
                                className="flex gap-2 items-center px-4 py-2 w-full text-sm text-left text-gray-700 hover:bg-gray-100"
                              >
                                <Users className="w-3.5 h-3.5" />
                                {t('member.menu.addClass')}
                              </button>
                              <button
                                onClick={() => {
                                  openEditModal(member)
                                  setOpenMenuId(null)
                                }}
                                className="flex gap-2 items-center px-4 py-2 w-full text-sm text-left text-gray-700 hover:bg-gray-100"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                                {t('member.menu.editInfo')}
                              </button>
                              <div className="my-1 border-t border-gray-100" />
                              <button
                                onClick={() => {
                                  handleDelete(member.userId, member.fullName || '')
                                  setOpenMenuId(null)
                                }}
                                className="flex gap-2 items-center px-4 py-2 w-full text-sm text-left text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                {t('member.menu.delete')}
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {!isLoading && filteredMembers.length > 0 && (
          <div className="flex justify-between items-center px-6 py-4 border-t border-gray-200">
            <span className="text-sm text-gray-500">
              {t('member.pagination.showing', { total: filteredMembers.length, start: page * pageSize + 1, end: Math.min((page + 1) * pageSize, filteredMembers.length) })}
            </span>
            {totalPages > 1 && (
              <div className="flex gap-2 items-center">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {t('member.pagination.prev')}
                </button>
                <span className="px-2 text-sm text-gray-700">
                  {page + 1} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('member.pagination.next')}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal */}
      <Dialog open={isModalOpen} onOpenChange={(open) => { if (!open) closeModal() }}>
      <DialogContent className="sm:max-w-md">
          <div>
            {editingMember ? (
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  {formData.fullName} {t('member.menu.editInfo')}
                </h2>
<select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="member">{t('member.role.learner')}</option>
                  <option value="admin">{t('member.role.admin')}</option>
                  <option value="admin_learner">{t('member.role.adminFull')}</option>
                </select>
              </div>
            ) : (
              <h2 className="mb-4 text-xl font-bold text-gray-900">
                {t('member.addButton')}
              </h2>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {editingMember ? (
                <>
                  {/* 수정 모드: 이름만 수정 가능 */}
                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">
                      {t('member.modal.nameLabel')}
                    </label>
                    <input
                      type="text"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500"
                      placeholder={t('member.modal.namePlaceholder')}
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">
                      {t('member.modal.emailReadonly')}
                    </label>
                    <input
                      type="email"
                      disabled
                      value={formData.email}
                      className="px-4 py-2 w-full text-gray-500 bg-gray-50 rounded-lg border border-gray-200"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">
                      {t('member.modal.emailLabel')}
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500"
                      placeholder="name@company.com"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">
                      {t('member.modal.fullNameLabel')}
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500"
                      placeholder={t('member.modal.namePlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">
                      {t('member.modal.roleLabel')}
                    </label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="member">{t('member.role.learner')}</option>
                      <option value="admin">{t('member.role.admin')}</option>
                      <option value="admin_learner">{t('member.role.adminFull')}</option>
                    </select>
                  </div>
                </>
              )}

              {editingMember && (
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    {t('member.filter.accountStatus')}
                  </label>
                  <select
                    value={editAccountStatus}
                    onChange={(e) => setEditAccountStatus(e.target.value)}
                    className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="PENDING">{t('member.status.pending')}</option>
                    <option value="INVITED">{t('member.status.invited')}</option>
                    <option value="ACTIVE">{t('member.status.active')}</option>
                  </select>
                </div>
              )}

              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  {t('member.modal.departmentLabel')}
                </label>
                <select
                  value={formData.departmentId || ''}
                  onChange={(e) => setFormData({ ...formData, departmentId: e.target.value ? Number(e.target.value) : undefined })}
                  className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">{t('member.modal.departmentNone')}</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  {t('member.modal.employeeIdLabel')}
                </label>
                <input
                  type="text"
                  value={formData.employeeId}
                  onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                  className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500"
                  placeholder="EMP001"
                />
              </div>

              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  {t('member.modal.positionLabel')}
                </label>
                <input
                  type="text"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500"
                  placeholder={t('member.modal.positionPlaceholder')}
                />
              </div>

              {/* 학습그룹 - 수정 모드에서만 표시 */}
              {editingMember && specialGroups.length > 0 && (
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    {t('member.filter.specialGroup')}
                  </label>
                  <div className="overflow-y-auto p-3 space-y-2 max-h-32 rounded-lg border border-gray-200">
                    {specialGroups.map((group) => (
                      <label key={group.id} className="flex gap-2 items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editFormSpecialGroupIds.includes(group.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setEditFormSpecialGroupIds([...editFormSpecialGroupIds, group.id])
                            } else {
                              setEditFormSpecialGroupIds(editFormSpecialGroupIds.filter(id => id !== group.id))
                            }
                          }}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">{group.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-4 space-x-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  {t('member.modal.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 text-white rounded-lg bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                >
                  {editingMember ? t('member.modal.edit') : t('member.modal.create')}
                </button>
              </div>
            </form>
          </div>
      </DialogContent>
      </Dialog>

      {/* Bulk Upload Modal */}
      <Dialog open={isBulkModalOpen} onOpenChange={(open) => { if (!open) { setIsBulkModalOpen(false); setCsvFile(null); setBulkDefaults({ role: 'member' }) } }}>
      <DialogContent className="sm:max-w-2xl">
          <div>
            <h2 className="mb-4 text-xl font-bold text-gray-900">{t('member.bulk.title')}</h2>

            <div className="space-y-6">
              {/* 안내 사항 */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="mb-2 font-semibold text-blue-900">📋 {t('member.bulk.csvTitle')}</h3>
                <p className="mb-3 text-sm text-blue-800">
                  {t('member.bulk.csvDescription')}
                </p>
                <p className="mb-2 text-sm font-semibold text-blue-900">{t('member.bulk.csvColumns')}</p>
                <p className="font-mono text-sm text-blue-800">
                  {t('member.bulk.csvColumnList')}
                </p>
                <div className="mt-3 text-xs text-blue-700">
                  <p className="mb-1 font-semibold">{t('member.bulk.csvNotes')}</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>{t('member.bulk.csvNote1')}</li>
                    <li>{t('member.bulk.csvNote2')}</li>
                    <li>{t('member.bulk.csvNote3')}</li>
                    <li>{t('member.bulk.csvNote4')}</li>
                    <li>{t('member.bulk.csvNote5')}</li>
                  </ul>
                </div>
              </div>

              {/* 기본값 설정 */}
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="mb-4 font-semibold text-gray-900">{t('member.bulk.defaultsTitle')}</h3>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    {t('member.bulk.defaultRoleLabel')}
                  </label>
                  <select
                    value={bulkDefaults.role}
                    onChange={(e) => setBulkDefaults({ ...bulkDefaults, role: e.target.value })}
                    className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="member">{t('member.role.learner')}</option>
                    <option value="admin">{t('member.role.admin')}</option>
                    <option value="admin_learner">{t('member.role.adminFull')}</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">{t('member.bulk.defaultRoleNote')}</p>
                </div>
              </div>

              {/* 파일 업로드 */}
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  {t('member.bulk.fileLabel')}
                </label>
                <div className="flex items-center mt-1 space-x-4">
                  <label className="flex-1 cursor-pointer">
                    <div className="flex justify-center items-center w-full h-32 rounded-lg border-2 border-gray-300 border-dashed transition-colors hover:border-primary-500 hover:bg-gray-50">
                      {csvFile ? (
                        <div className="text-center">
                          <p className="text-sm font-medium text-gray-900">{csvFile.name}</p>
                          <p className="mt-1 text-xs text-gray-500">
                            {(csvFile.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                      ) : (
                        <div className="text-center">
                          <Upload className="mx-auto w-8 h-8 text-gray-400" />
                          <p className="mt-2 text-sm text-gray-600">
                            {t('member.bulk.fileClick')}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            {t('member.bulk.fileDrag')}
                          </p>
                        </div>
                      )}
                    </div>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {/* 버튼 */}
              <div className="flex justify-end pt-4 space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsBulkModalOpen(false)
                    setCsvFile(null)
                    setBulkDefaults({
                      role: 'member',
                    })
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  {t('member.bulk.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleBulkUpload}
                  disabled={!csvFile || bulkCreateMutation.isPending}
                  className="px-4 py-2 text-white rounded-lg bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                >
                  {bulkCreateMutation.isPending ? t('member.bulk.submitting') : t('member.bulk.submit')}
                </button>
              </div>
            </div>
          </div>
      </DialogContent>
      </Dialog>

      {/* 클래스에 추가 Modal */}
      <Dialog open={isClassModalOpen && !!selectedMemberForClass} onOpenChange={(open) => { if (!open) closeClassModal() }}>
      <DialogContent className="sm:max-w-md">
          <div>
            <h2 className="mb-4 text-xl font-bold text-gray-900">
              {t('member.classModal.titleSimple')}
            </h2>
            <p className="mb-4 text-sm text-gray-600">
              {t('member.leaveClassModal.info', { name: selectedMemberForClass?.fullName })}
            </p>

            <form onSubmit={handleAddToClass} className="space-y-4">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  {t('member.leaveClassModal.selectClassRequired')}
                </label>
                {isLoadingClasses ? (
                  <div className="text-sm text-gray-500">{t('common.loading')}</div>
                ) : classes.length === 0 ? (
                  <div className="flex items-center p-4 space-x-2 text-amber-700 bg-amber-50 rounded-lg border border-amber-200">
                    <AlertCircle className="w-5 h-5" />
                    <span className="text-sm">{t('member.leaveClassModal.noClassesAvailable')}</span>
                  </div>
                ) : (
                  <select
                    required
                    value={selectedClassId || ''}
                    onChange={(e) => setSelectedClassId(e.target.value ? Number(e.target.value) : null)}
                    className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">{t('member.classModal.selectPlaceholder')}</option>
                    {classes.map((cls) => (
                      <option key={cls.id} value={cls.id}>
                        {cls.name} ({cls.currentStudents}/{cls.maxStudents})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="flex justify-end pt-4 space-x-3">
                <button
                  type="button"
                  onClick={closeClassModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  {t('member.classModal.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={addToClassMutation.isPending || classes.length === 0 || !selectedClassId}
                  className="px-4 py-2 text-white rounded-lg bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addToClassMutation.isPending ? t('member.classModal.submitting') : t('member.leaveClassModal.addToClass')}
                </button>
              </div>
            </form>
          </div>
      </DialogContent>
      </Dialog>

      {/* {t('member.leaveClass')} 확인 Modal */}
      <Dialog open={isLeaveClassModalOpen && !!leaveClassInfo} onOpenChange={(open) => { if (!open) closeLeaveClassModal() }}>
      <DialogContent className="sm:max-w-md">
          <div>
            <div className="flex gap-3 items-center mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <UserMinus className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                {t('member.leaveClassModal.title')}
              </h2>
            </div>

            <div className="space-y-4">
              {/* 탈퇴 정보 */}
              <div className="p-4 space-y-2 bg-gray-50 rounded-lg">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">{t('member.leaveClassModal.employee')}</span>
                  <span className="text-sm font-medium text-gray-900">{leaveClassInfo?.fullName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">{t('member.leaveClassModal.className')}</span>
                  <span className="text-sm font-medium text-gray-900">{leaveClassInfo?.className}</span>
                </div>
                {leaveClassInfo?.productName && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">{t('member.leaveClassModal.ongoingProduct')}</span>
                    <span className="text-sm font-medium text-primary-600">{leaveClassInfo?.productName}</span>
                  </div>
                )}
              </div>

              {/* 경고 메시지 */}
              <div className="flex gap-3 items-start p-4 bg-amber-50 rounded-lg border border-amber-200">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="mb-1 font-medium">{t('member.leaveClassModal.warningTitle')}</p>
                  <ul className="list-disc list-inside space-y-0.5 text-amber-700">
                    <li>{t('member.leaveClassModal.warningProgress')}</li>
                    <li>{t('member.leaveClassModal.warningRecords')}</li>
                    <li>{t('member.leaveClassModal.warningReassign')}</li>
                  </ul>
                </div>
              </div>

              {/* 버튼 */}
              <div className="flex justify-end pt-2 space-x-3">
                <button
                  type="button"
                  onClick={closeLeaveClassModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  {t('member.leaveClassModal.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleLeaveClass}
                  disabled={removeFromClassMutation.isPending}
                  className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {removeFromClassMutation.isPending ? t('member.bulk.submitting') : t('member.leaveClassModal.confirmButton')}
                </button>
              </div>
            </div>
          </div>
      </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={isResetPasswordModalOpen && !!resetPasswordTarget} onOpenChange={(open) => { if (!open) { setIsResetPasswordModalOpen(false); setResetPasswordTarget(null); setResetPasswordValue('12341234') } }}>
      <DialogContent className="sm:max-w-md">
          <div>
            <div className="flex gap-3 items-center mb-4">
              <div className="p-2 bg-orange-100 rounded-full">
                <KeyRound className="w-6 h-6 text-orange-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                {t('member.resetPasswordModal.title')}
              </h2>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-700">
                {t('member.resetPasswordModal.description', { name: resetPasswordTarget?.fullName })}
              </p>

              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  {t('member.resetPasswordModal.label')}
                </label>
                <input
                  type="text"
                  value={resetPasswordValue}
                  onChange={(e) => setResetPasswordValue(e.target.value)}
                  className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="12341234"
                />
              </div>

              <div className="flex gap-3 items-start p-4 bg-amber-50 rounded-lg border border-amber-200">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  {t('member.resetPasswordModal.warning')}
                </p>
              </div>

              <div className="flex justify-end pt-2 space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsResetPasswordModalOpen(false)
                    setResetPasswordTarget(null)
                    setResetPasswordValue('12341234')
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  {t('member.resetPasswordModal.cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!resetPasswordTarget) return
                    resetPasswordMutation.mutate({
                      userId: resetPasswordTarget.userId,
                      newPassword: resetPasswordValue.trim() || undefined,
                    })
                  }}
                  disabled={resetPasswordMutation.isPending}
                  className="px-4 py-2 text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50"
                >
                  {resetPasswordMutation.isPending ? t('member.resetPasswordModal.confirming') : t('member.resetPasswordModal.confirm')}
                </button>
              </div>
            </div>
          </div>
      </DialogContent>
      </Dialog>

      {/* Send Credentials Confirmation Modal */}
      <Dialog open={isSendCredentialsModalOpen} onOpenChange={setIsSendCredentialsModalOpen}>
      <DialogContent className="sm:max-w-md">
          <div>
            <div className="flex gap-3 items-center mb-4">
              <div className="p-2 bg-blue-100 rounded-full">
                <Mail className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                {t('member.sendCredentialsModal.title')}
              </h2>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-700">
                {t('member.sendCredentialsModal.info', { count: selectedMemberIds.size })}
              </p>

              {/* Member list */}
              <div className="overflow-y-auto p-4 max-h-48 bg-gray-50 rounded-lg">
                <ul className="space-y-1">
                  {members
                    .filter(m => selectedMemberIds.has(m.userId))
                    .map(m => (
                      <li key={m.userId} className="text-sm text-gray-700">
                        &bull; {m.email} ({m.fullName})
                      </li>
                    ))}
                </ul>
              </div>

              {/* Warning about password replacement */}
              <div className="flex gap-3 items-start p-4 bg-amber-50 rounded-lg border border-amber-200">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  {t('member.sendCredentialsModal.warning')}
                </p>
              </div>

              <div className="flex justify-end pt-2 space-x-3">
                <button
                  type="button"
                  onClick={() => setIsSendCredentialsModalOpen(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  {t('member.sendCredentialsModal.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleSendCredentials}
                  disabled={sendCredentialsMutation.isPending}
                  className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {sendCredentialsMutation.isPending ? t('member.sendCredentialsModal.confirming') : t('member.sendCredentialsModal.sendButton')}
                </button>
              </div>
            </div>
          </div>
      </DialogContent>
      </Dialog>
    </div>
  )
}

